"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { supabaseBrowser } from "@/lib/supabase/browser";
import { detectInAppBrowser } from "@/lib/auth/inAppBrowser";
import { Factory, Mail, Lock, ArrowRight, AlertCircle, CheckCircle, Eye, EyeOff, Zap, Ruler, Shield, HelpCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { CLAIMS } from "@/lib/claims";
import { classifyAuthError, AUTH_ERROR_MESSAGE_KEY, type AuthErrorCode } from "@/lib/auth/authErrors";
import { jobsPathForCurrentLocale } from "@/lib/auth/localePaths";

// Module-level Supabase client (avoids recreating on every render)
const sb = supabaseBrowser();

/**
 * Password strength calculator for signup form
 * Returns level 0-4 with label key and color for the strength bar
 */
function getPasswordStrength(password: string): { level: 0 | 1 | 2 | 3 | 4; labelKey: string; color: string; width: string } {
  if (!password) return { level: 0, labelKey: "", color: "bg-slate-200", width: "0%" };
  if (password.length < 8) return { level: 1, labelKey: "password.strength.tooShort", color: "bg-red-500", width: "25%" };
  
  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  
  const score = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
  
  if (score <= 1) return { level: 2, labelKey: "password.strength.weak", color: "bg-orange-500", width: "50%" };
  if (score <= 2) return { level: 3, labelKey: "password.strength.good", color: "bg-yellow-500", width: "75%" };
  return { level: 4, labelKey: "password.strength.strong", color: "bg-emerald-500", width: "100%" };
}

/**
 * Helper to detect if a Supabase error indicates the email is already registered.
 * Used for Google-collision detection on sign-up flow.
 */
function isAlreadyRegisteredError(err: { message?: string; status?: number } | null): boolean {
  if (!err) return false;
  const msg = (err.message || "").toLowerCase();
  const patterns = ["already registered", "already exists", "user already", "registered"];
  return patterns.some((p) => msg.includes(p)) || err.status === 409;
}

/**
 * Helper to detect network/connection errors (e.g., Supabase project paused).
 * "Failed to fetch" happens when the browser can't reach the Supabase API.
 */
function isNetworkError(err: { message?: string } | null): boolean {
  if (!err) return false;
  const msg = (err.message || "").toLowerCase();
  return msg.includes("failed to fetch") || msg.includes("network") || msg.includes("timeout") || msg.includes("aborted");
}

/**
 * Helper to check email status (what auth providers exist for this email).
 * Used for AUTO-LINK: one OptiStack account per email.
 */
async function checkEmailStatus(
  email: string
): Promise<{ exists: boolean; providers: string[]; known: boolean }> {
  try {
    const res = await fetch(`/api/auth/email-status?email=${encodeURIComponent(email)}`);
    if (!res.ok) {
      // The lookup FAILED. That is not the same as "this email has no account",
      // and collapsing the two is how an existing user could be told they have
      // no account. `known: false` means: carry on and let the password attempt
      // decide.
      return { exists: false, providers: [], known: false };
    }
    const body = await res.json();
    return { exists: Boolean(body.exists), providers: body.providers ?? [], known: true };
  } catch {
    return { exists: false, providers: [], known: false };
  }
}

export default function LoginContent({ initialMode = "signin" }: { initialMode?: "signin" | "signup" }) {
  const router = useRouter();
  const t = useTranslations("auth");
  
  // Ref to prevent double form submission
  const isSubmitting = useRef(false);
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showGoogleCollisionCTA, setShowGoogleCollisionCTA] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  // In-app webview detection: Google OAuth is blocked in embedded browsers
  // (disallowed_useragent) and creates NO account, so we steer to email/password.
  const [inApp, setInApp] = useState<{ isInApp: boolean; matched: string | null }>({ isInApp: false, matched: null });
  const [linkCopied, setLinkCopied] = useState(false);
  const [error, setError] = useState("");

  /**
   * Turn an error code into a sentence. Anything not in the table resolves to
   * the generic message rather than being printed verbatim, which is what used
   * to leak Supabase's developer-facing text onto the page.
   */
  const errorText = (code: string): string => {
    const key = AUTH_ERROR_MESSAGE_KEY[code as AuthErrorCode];
    return t(`errors.${key ?? "unexpectedError"}`);
  };
  const [success, setSuccess] = useState("");
  const [mode, setMode] = useState<"signin" | "signup">(initialMode);
  const [showEmailVerificationMessage, setShowEmailVerificationMessage] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

  // Detect in-app webview once on mount (client-only).
  useEffect(() => {
    setInApp(detectInAppBrowser());
  }, []);


  // Password strength for signup mode
  const passwordStrength = getPasswordStrength(password);

  // Check for access denial reason in URL. Read from window.location (client-only)
  // rather than useSearchParams() so this component server-renders its full markup
  // instead of the Suspense fallback during static prerender.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get("reason");
    const errorParam = params.get("e");
    if (reason === "not_allowed") {
      setError("ACCESS_NOT_GRANTED");
    } else if (reason === "auth_required") {
      setError("AUTH_REQUIRED");
    } else if (errorParam) {
      setError(classifyAuthError(errorParam));
    }
  }, []);

  // Check if already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await sb.auth.getSession();
      if (session) {
        // Redirect to next param if present, otherwise /jobs
        const next = new URLSearchParams(window.location.search).get("next");
        const destination = next && next.startsWith("/") ? next : jobsPathForCurrentLocale();
        router.push(destination);
      }
    };
    checkSession();
  }, [sb.auth, router]);

  async function handleGoogleSignIn() {
    setError("");
    setIsGoogleLoading(true);

    try {
      // Build callback URL with next param - ALWAYS include a destination
      // Get current locale from URL path, validate against supported locales
      // Falls back to 'en' if user is on /login (no locale prefix) or any other invalid path
      const SUPPORTED_LOCALES = ['en', 'pt-BR', 'es', 'it', 'ru'];
      const firstSegment = window.location.pathname.split('/')[1];
      const locale = SUPPORTED_LOCALES.includes(firstSegment) ? firstSegment : 'en';

      // Build destination, ignore any leaked /login-prefixed values
      const next = new URLSearchParams(window.location.search).get("next");
      const isValidNext =
        next &&
        next.startsWith("/") &&
        !next.startsWith("/login") &&
        !next.startsWith("/auth");
      const destination = isValidNext ? next : `/${locale}/jobs`;

      const callbackUrl = new URL(`/${locale}/auth/callback`, window.location.origin);
      callbackUrl.searchParams.set("next", destination);

      const { error: authError } = await sb.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: callbackUrl.toString(),
        },
      });

      if (authError) {
        setError(classifyAuthError(authError.message, authError.status));
        setIsGoogleLoading(false);
      }
    } catch (err) {
      setError("GOOGLE_SIGN_IN_FAILED");
      setIsGoogleLoading(false);
    }
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    
    setError("");
    setSuccess("");
    setPasswordError("");
    setConfirmPasswordError("");
    setShowGoogleCollisionCTA(false);
    setIsLoading(true);

    const trimmedEmail = email.trim().toLowerCase();

    try {
      // SIGN UP FLOW: Client-side validation before calling Supabase
      if (mode === "signup") {
        // Validate password length >= 8
        if (password.length < 8) {
          setPasswordError(t("errors.passwordTooShort"));
          setIsLoading(false);
          isSubmitting.current = false;
          return;
        }

        // Validate confirm password matches
        if (password !== confirmPassword) {
          setConfirmPasswordError(t("errors.passwordsDontMatch"));
          setIsLoading(false);
          isSubmitting.current = false;
          return;
        }
      }

      // CHECK EMAIL STATUS FOR AUTO-LINK BEHAVIOR
      const emailStatus = await checkEmailStatus(trimmedEmail);

      if (mode === "signup") {
        // Sign up flow with AUTO-LINK checks
        if (emailStatus.exists) {
          if (emailStatus.providers.includes("google") && !emailStatus.providers.includes("email")) {
            // Google-only account trying to sign up with email - show collision CTA
            setError("GOOGLE_COLLISION");
            setShowGoogleCollisionCTA(true);
            setIsLoading(false);
            isSubmitting.current = false;
            return;
          }
          if (emailStatus.providers.includes("email")) {
            // Email account already exists
            setError("EMAIL_EXISTS");
            setIsLoading(false);
            isSubmitting.current = false;
            return;
          }
        }

        // Get current locale from URL path, validate against supported locales
        // Falls back to 'en' if user is on /login (no locale prefix) or any other invalid path
        const SUPPORTED_LOCALES = ['en', 'pt-BR', 'es', 'it', 'ru'];
        const firstSegment = window.location.pathname.split('/')[1];
        const locale = SUPPORTED_LOCALES.includes(firstSegment) ? firstSegment : 'en';
        
        const { data: signUpData, error: authError } = await sb.auth.signUp({
          email: trimmedEmail,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/${locale}/auth/callback?next=/${locale}/jobs`,
          },
        });

        if (authError) {
          // Check for network/connection errors first (e.g., Supabase project paused)
          if (isNetworkError(authError)) {
            setError("SERVICE_UNAVAILABLE");
            setIsLoading(false);
            isSubmitting.current = false;
            return;
          }
          // Check for Google-collision via error message
          if (isAlreadyRegisteredError(authError)) {
            // Check if it's a Google account
            const recheckStatus = await checkEmailStatus(trimmedEmail);
            if (recheckStatus.exists && recheckStatus.providers.includes("google") && !recheckStatus.providers.includes("email")) {
              setError("GOOGLE_COLLISION");
              setShowGoogleCollisionCTA(true);
            } else {
              setError("EMAIL_EXISTS");
            }
          } else {
            setError(classifyAuthError(authError.message, authError.status));
          }
          setIsLoading(false);
          isSubmitting.current = false;
          return;
        }

        // Auto-login after signup (skip email verification)
        // If signup succeeded (user created), immediately sign them in
        if (signUpData?.user) {
          const { data: signInData, error: signInError } = await sb.auth.signInWithPassword({
            email: trimmedEmail,
            password,
          });

          if (signInError) {
            // If auto-login fails (e.g., email not confirmed in Supabase settings),
            // fall back to showing verification message
            if (signInError.message.includes("Email not confirmed")) {
              setPendingEmail(trimmedEmail);
              setShowEmailVerificationMessage(true);
              setSuccess("");
              setIsLoading(false);
              isSubmitting.current = false;
              return;
            }
            setError(classifyAuthError(signInError.message, signInError.status));
            setIsLoading(false);
            isSubmitting.current = false;
            return;
          }

          if (signInData?.session) {
            setShowSuccessOverlay(true);
            setTimeout(() => {
              router.push(jobsPathForCurrentLocale());
              router.refresh();
            }, 1500);
            setIsLoading(false);
            isSubmitting.current = false;
            return;
          }
        }

        // Fallback: If session exists directly from signup (email confirmation disabled in Supabase)
        if (signUpData?.session) {
          setShowSuccessOverlay(true);
          setTimeout(() => {
            router.push(jobsPathForCurrentLocale());
            router.refresh();
          }, 1500);
          setIsLoading(false);
          isSubmitting.current = false;
          return;
        }

        setSuccess("Account created successfully!");
        setIsLoading(false);
        isSubmitting.current = false;
        return;
      }

      // Sign in flow - provide specific error messages
      
      // CASE 1: Email exists but only via Google → Tell them to use Google
      if (emailStatus.exists && emailStatus.providers.includes("google") && !emailStatus.providers.includes("email")) {
        setError("GOOGLE_ONLY");
        setShowGoogleCollisionCTA(true);
        setIsLoading(false);
        isSubmitting.current = false;
        return;
      }
      
      // CASE 1b: We KNOW there is no account for this email. Say so, and offer
      // the thing that actually helps, which is signing up. Only when the
      // lookup succeeded: on `known: false` fall through to the password
      // attempt rather than guessing.
      if (emailStatus.known && !emailStatus.exists) {
        setError("NO_ACCOUNT_FOUND");
        setIsLoading(false);
        isSubmitting.current = false;
        return;
      }

      // CASE 2: Attempt login directly - let Supabase handle validation
      const { error: authError } = await sb.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });

      if (authError) {
        // Check for network/connection errors first (e.g., Supabase project paused)
        if (isNetworkError(authError)) {
          setError("SERVICE_UNAVAILABLE");
          setIsLoading(false);
          isSubmitting.current = false;
          return;
        }
        // Email exists but password was wrong
        if (authError.message.includes("Invalid login credentials")) {
          setError("WRONG_PASSWORD");
        } else if (authError.message.includes("Email not confirmed") || authError.message.includes("email not confirmed")) {
          setPendingEmail(trimmedEmail);
          setShowEmailVerificationMessage(true);
          setError("");
          setIsLoading(false);
          isSubmitting.current = false;
          return;
        } else {
          setError(classifyAuthError(authError.message, authError.status));
        }
        setIsLoading(false);
        isSubmitting.current = false;
        return;
      }

      // Success! Show overlay and redirect
      setShowSuccessOverlay(true);
      const next = new URLSearchParams(window.location.search).get("next");
      const destination = next && next.startsWith("/") ? next : jobsPathForCurrentLocale();
      setTimeout(() => {
        router.push(destination);
        router.refresh();
      }, 1500);
    } catch (err) {
      setError("UNEXPECTED");
      setIsLoading(false);
      isSubmitting.current = false;
    }
  }

  return (
    <div className="min-h-[100dvh] bg-[#f8f9fb] flex items-center justify-center p-4 py-8 lg:py-4 relative overflow-y-auto">
      {/* Subtle radial glow behind card */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(226,133,48,0.04) 0%, transparent 70%)',
        }}
      />
      {/* Very faint grid texture */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%2315233a' stroke-width='0.5'%3E%3Cpath d='M0 20h40M20 0v40'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px',
        }}
      />
      
      <div className="relative w-full max-w-[420px] lg:max-w-[860px] my-auto lg:flex lg:items-center lg:justify-center lg:gap-8">
        {/* Card column (unchanged on mobile) */}
        <div className="w-full lg:w-[420px] lg:shrink-0">
        {/* Main card */}
        <div 
          className="relative bg-white rounded-2xl"
          style={{
            boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.06), 0 16px 40px rgba(15,23,42,0.08)',
            border: '1px solid rgba(15,23,42,0.06)',
          }}
        >
          {/* Inner highlight */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-90" />
          
          {/* Premium Success Redirect Overlay */}
          {showSuccessOverlay && (
            <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center z-20 animate-fadeIn">
              <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4 animate-scaleIn">
                <CheckCircle size={28} className="text-emerald-600" />
              </div>
              <p className="text-lg font-semibold text-slate-900">{t("success.welcomeToOptiStack")}</p>
              <p className="text-sm text-slate-500 mt-1">{t("success.settingUpWorkspace")}</p>
              <div className="mt-4 h-1 w-32 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full animate-progressBar" />
              </div>
            </div>
          )}
          
          {/* Header */}
          <div className="px-8 pt-10 pb-8 text-center">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div 
                className="h-14 w-14 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #e28530 0%, #c9742a 100%)',
                  boxShadow: '0 4px 12px rgba(226,133,48,0.25), inset 0 1px 0 rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <Factory size={26} className="text-white" />
              </div>
            </div>
            
            <h1 className="text-[22px] font-semibold text-slate-900 mb-2 tracking-tight">
              {mode === "signin"
                ? t("login.welcomeBack")
                : t("login.trialHeading", { days: CLAIMS.trialDays })}
            </h1>
            <p className="text-slate-500 text-sm leading-relaxed">
              {mode === "signin"
                ? t("login.signInToContinue")
                : t("login.trialSubtitle")}
            </p>
            
            {/* Trial badge - conversion critical (hidden on lg; panel covers desktop) */}
            <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 lg:hidden">
              <CheckCircle size={13} className="text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">{t("login.trialBadge")}</span>
            </div>
          </div>

          {/* Email Verification Required Message */}
          {showEmailVerificationMessage && (
            <div className="px-8 pb-8 pt-2">
              <div className="p-5 rounded-xl bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-3 mb-3">
                  <Mail size={20} className="flex-shrink-0 mt-0.5 text-amber-600" />
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-1">{t("verification.checkYourEmail")}</h3>
                    <p className="text-sm text-amber-700 leading-relaxed">
                      {t("verification.verificationSent", { email: pendingEmail })}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  <button
                    type="button"
                    disabled={resendLoading || resendCooldown > 0}
                    onClick={async () => {
                      setResendLoading(true);
                      try {
                        // Get current locale from URL, validate against supported locales
                        // Falls back to 'en' if user is on /login (no locale prefix) or any other invalid path
                        const SUPPORTED_LOCALES = ['en', 'pt-BR', 'es', 'it', 'ru'];
                        const firstSegment = window.location.pathname.split('/')[1];
                        const locale = SUPPORTED_LOCALES.includes(firstSegment) ? firstSegment : 'en';
                        
                        const { error } = await sb.auth.resend({
                          type: 'signup',
                          email: pendingEmail,
                          options: {
                            emailRedirectTo: `${window.location.origin}/${locale}/auth/callback?next=/${locale}/jobs`,
                          },
                        });
                        if (error) {
                          setError(classifyAuthError(error.message, error.status));
                        } else {
                          setSuccess(t("success.emailResent"));
                          // Start 60-second cooldown
                          setResendCooldown(60);
                          const interval = setInterval(() => {
                            setResendCooldown(prev => {
                              if (prev <= 1) {
                                clearInterval(interval);
                                return 0;
                              }
                              return prev - 1;
                            });
                          }, 1000);
                        }
                      } catch {
                        setError("RESEND_FAILED");
                      }
                      setResendLoading(false);
                    }}
                    className="w-full py-2.5 px-4 rounded-lg text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {resendLoading ? t("verification.sending") : resendCooldown > 0 ? t("verification.resendIn", { seconds: resendCooldown }) : t("verification.resendEmail")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailVerificationMessage(false);
                      setPendingEmail("");
                      setEmail("");
                      setPassword("");
                      setConfirmPassword("");
                    }}
                    className="text-sm text-amber-600 hover:text-amber-800 transition-colors"
                  >
                    {t("verification.differentEmail")}
                  </button>
                </div>
                <p className="mt-3 text-xs text-amber-600">
                  {/* Spam folder hint - not translated as it's static help text */}
                </p>
              </div>
              {error && (
                <div className="flex items-start gap-3 p-3.5 rounded-xl bg-red-50 border border-red-100 text-red-700 mt-4">
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5 text-red-500" />
                  <p className="text-sm">
                    {errorText(error)}
                  </p>
                </div>
              )}
              {success && (
                <div className="flex items-start gap-3 p-3.5 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 mt-4">
                  <CheckCircle size={18} className="flex-shrink-0 mt-0.5 text-emerald-500" />
                  <p className="text-sm">{success}</p>
                </div>
              )}
            </div>
          )}

          {/* Form */}
          {!showEmailVerificationMessage && (
          <form onSubmit={handleSignIn} className="px-8 pb-6 space-y-4">
            {/* Error message with contextual actions */}
            {error && (
              <div className="p-3.5 rounded-xl bg-red-50 border border-red-100">
                <div className="flex items-start gap-3 text-red-700">
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5 text-red-500" />
                  <div className="text-sm flex-1">
                    <p className={error === "NO_ACCOUNT_FOUND" || error === "WRONG_PASSWORD" ? "font-medium" : undefined}>
                      {errorText(error)}
                    </p>
                  </div>
                </div>
                {/* No Account Found → Offer to Sign Up */}
                {error === "NO_ACCOUNT_FOUND" && (
                  <button
                    type="button"
                    onClick={() => {
                      setError("");
                      setMode("signup");
                    }}
                    className="w-full mt-4 flex items-center justify-center gap-2 py-3 px-6 rounded-xl
                               bg-amber-500 hover:bg-amber-600
                               text-white font-semibold text-sm
                               transition-all duration-200 hover:shadow-md active:scale-[0.99]"
                  >
                    <ArrowRight size={16} />
                    {t("login.createAccountBtn")}
                  </button>
                )}
                {/* Google Collision CTA Button */}
                {showGoogleCollisionCTA && (
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading}
                    className="w-full mt-4 flex items-center justify-center gap-3 py-3 px-6 rounded-xl
                               bg-white hover:bg-slate-50
                               text-slate-700 font-medium text-sm
                               disabled:opacity-60 disabled:cursor-not-allowed
                               transition-all duration-200 hover:shadow-sm active:scale-[0.99]"
                    style={{
                      border: '1px solid rgba(15,23,42,0.12)',
                      boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                    }}
                  >
                    {isGoogleLoading ? (
                      <div className="h-5 w-5 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin" />
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        {t("login.continueWithGoogle")}
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Success message */}
            {success && (
              <div className="flex items-start gap-3 p-3.5 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700">
                <CheckCircle size={18} className="flex-shrink-0 mt-0.5 text-emerald-500" />
                <p className="text-sm">{success}</p>
              </div>
            )}

            {/* Google Sign In Button — hidden inside in-app webviews where
                Google blocks OAuth (disallowed_useragent). Show an open-in-browser
                prompt instead so the lead can still get in via email/password. */}
            {inApp.isInApp ? (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">Google sign-in isn’t available in this app’s browser{inApp.matched ? ` (${inApp.matched})` : ""}.</p>
                <p className="mt-1 text-amber-800">Open this page in Safari or Chrome to use Google, or just sign in with email below.</p>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(window.location.href).then(() => {
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 1500);
                    });
                  }}
                  className="mt-2 inline-flex items-center gap-2 rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  {linkCopied ? "Link copied, paste in your browser" : "Copy link to open in browser"}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading || isLoading}
                className="w-full flex items-center justify-center gap-3 py-3 px-6 rounded-xl
                           bg-white hover:bg-slate-50
                           text-slate-700 font-medium text-sm
                           disabled:opacity-60 disabled:cursor-not-allowed
                           transition-all duration-200 hover:shadow-sm active:scale-[0.99]"
                style={{
                  border: '1px solid rgba(15,23,42,0.12)',
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                }}
              >
                {isGoogleLoading ? (
                  <div className="h-5 w-5 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin" />
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t("login.continueWithGoogle")}
                  </>
                )}
              </button>
            )}

            {/* Divider */}
            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-100"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-white text-[11px] font-medium text-slate-400 uppercase tracking-wider">
                  {t("login.email")}
                </span>
              </div>
            </div>

            {/* Email field */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                {t("login.email")}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Mail size={17} className="text-slate-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("login.emailPlaceholder")}
                  required
                  autoFocus
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 rounded-lg text-slate-900 text-sm placeholder-slate-400 
                             border border-slate-200 
                             focus:outline-none focus:bg-white focus:border-[#e28530]/50 focus:ring-2 focus:ring-[#e28530]/10 
                             transition-all duration-200"
                />
              </div>
            </div>

            {/* Password field */}
            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                {t("login.password")}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock size={17} className="text-slate-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError("");
                  }}
                  placeholder="••••••••"
                  required
                  minLength={mode === "signup" ? 8 : 6}
                  className={`w-full pl-10 pr-12 py-2.5 bg-slate-50 rounded-lg text-slate-900 text-sm placeholder-slate-400 
                             border ${passwordError ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : 'border-slate-200 focus:border-[#e28530]/50 focus:ring-[#e28530]/10'} 
                             focus:outline-none focus:bg-white focus:ring-2 
                             transition-all duration-200`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              {passwordError && (
                <p className="text-xs text-red-600 mt-1">{passwordError}</p>
              )}
              {/* Password strength indicator - only shown on signup */}
              {mode === "signup" && password && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${passwordStrength.color} rounded-full transition-all duration-200 ease-out`}
                      style={{ width: passwordStrength.width }}
                    />
                  </div>
                  {passwordStrength.labelKey && (
                    <span className={`text-xs font-medium ${
                      passwordStrength.level === 1 ? 'text-red-600' :
                      passwordStrength.level === 2 ? 'text-orange-600' :
                      passwordStrength.level === 3 ? 'text-yellow-600' :
                      'text-emerald-600'
                    }`}>
                      {t(passwordStrength.labelKey)}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Confirm Password field - animated slide-in on sign up */}
            <div 
              className={`overflow-hidden transition-all duration-200 ease-out ${
                mode === "signup" ? "max-h-[100px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="space-y-1.5">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">
                  {t("login.confirmPassword")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Lock size={17} className="text-slate-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setConfirmPasswordError("");
                    }}
                    placeholder="••••••••"
                    required={mode === "signup"}
                    minLength={8}
                    className={`w-full pl-10 pr-12 py-2.5 bg-slate-50 rounded-lg text-slate-900 text-sm placeholder-slate-400 
                               border ${confirmPasswordError ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : 'border-slate-200 focus:border-[#e28530]/50 focus:ring-[#e28530]/10'} 
                               focus:outline-none focus:bg-white focus:ring-2 
                               transition-all duration-200`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {confirmPasswordError && (
                  <p className="text-xs text-red-600 mt-1">{confirmPasswordError}</p>
                )}
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading || isGoogleLoading}
              className="w-full relative group flex items-center justify-center gap-2 py-3 px-6 rounded-xl text-white font-semibold text-sm
                         disabled:opacity-60 disabled:cursor-not-allowed
                         transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
              style={{
                background: 'linear-gradient(180deg, #e9923a 0%, #d97b24 100%)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.07), 0 4px 12px rgba(226,133,48,0.20), inset 0 1px 0 rgba(255,255,255,0.15)',
                border: '1px solid rgba(185,100,30,0.3)',
              }}
            >
              {isLoading ? (
                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {mode === "signin" ? t("login.signIn") : t("login.createFreeAccount")}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>

            {/* Toggle sign in / sign up */}
            <div className="text-center pt-1 space-y-2">
              <button
                type="button"
                onClick={() => {
                  setMode(mode === "signin" ? "signup" : "signin");
                  setError("");
                  setSuccess("");
                  setPasswordError("");
                  setConfirmPasswordError("");
                  setConfirmPassword("");
                  setShowGoogleCollisionCTA(false);
                  setShowEmailVerificationMessage(false);
                }}
                className="text-sm text-slate-500 hover:text-[#d97b24] transition-colors"
              >
                {mode === "signin"
                  ? `${t("login.noAccount")} ${t("login.signUp")}`
                  : `${t("login.haveAccount")} ${t("login.logIn")}`}
              </button>
              {mode === "signin" && (
                <div>
                  <a
                    href="/forgot-password"
                    className="text-sm text-slate-500 hover:text-[#d97b24] transition-colors"
                  >
                    {t("login.forgotPassword")}
                  </a>
                </div>
              )}
            </div>
          </form>
          )}

          {/* Footer: Social Proof + Stats */}
          <div className="px-8 py-5 bg-slate-50/70 border-t border-slate-100">
            {/* Trust headline (hidden on lg; shown in the desktop side panel) */}
            <p className="text-center text-xs text-slate-500 mb-3 lg:hidden">
              Built by a slitter tooling maker, in business since 1976
            </p>

            {/* Stats row - slitting-specific (hidden on lg; side panel covers desktop) */}
            <div className="flex flex-wrap justify-center gap-2 mb-4 lg:hidden">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-100 shadow-sm">
                <Ruler size={12} className="text-[#e28530]" />
                <span className="text-[11px] text-slate-600"><strong className="font-semibold">0.01mm</strong> precision</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-100 shadow-sm">
                <Zap size={12} className="text-[#e28530]" />
                <span className="text-[11px] text-slate-600"><strong className="font-semibold">{CLAIMS.solveTimeShort}</strong> avg setup</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-100 shadow-sm">
                <Shield size={12} className="text-[#e28530]" />
                <span className="text-[11px] text-slate-600"><strong className="font-semibold">Zero</strong> calc errors</span>
              </div>
            </div>
            
            {/* Help email (hidden on lg to keep the card within an 800px viewport) */}
            <div className="flex items-center justify-center gap-2 text-xs text-slate-500 pt-2 lg:hidden">
              <HelpCircle size={14} className="text-slate-400" />
              <span>Need help?</span>
              <a 
                href="mailto:info@maxwellslitters.com" 
                className="text-[#d97b24] hover:text-[#c86d1d] font-medium transition-colors"
              >
                info@maxwellslitters.com
              </a>
            </div>
            
            {/* Legal + terms */}
            <div className="text-center space-y-2 pt-2 border-t border-slate-100">
              <p className="text-[11px] text-slate-400">
                By continuing, you agree to our{" "}
                <a href="/terms" className="text-slate-500 hover:text-[#d97b24] transition-colors">
                  Terms
                </a>
                {" "}and{" "}
                <a href="/privacy" className="text-slate-500 hover:text-[#d97b24] transition-colors">
                  Privacy Policy
                </a>
              </p>
              <div className="flex items-center justify-center gap-3 text-[11px] text-slate-400">
                <a href="/terms" className="hover:text-[#d97b24] transition-colors">Terms</a>
                <span className="text-slate-200">•</span>
                <a href="/privacy" className="hover:text-[#d97b24] transition-colors">Privacy</a>
                <span className="text-slate-200">•</span>
                <a href="/contact" className="hover:text-[#d97b24] transition-colors">Contact</a>
              </div>
            </div>
          </div>
        </div>

        {/* Trust + status footer */}
        <div className="mt-6 flex items-center justify-center gap-3 text-xs text-slate-400">
          <span>Secure portal</span>
          <span className="text-slate-200">•</span>
          <span>
            System status: <span className="font-mono text-emerald-600">Online</span>
          </span>
        </div>
        
        {/* Brand footer */}
        <p className="mt-4 text-center text-xs text-slate-400">
          © 2026 OptiStack · Maxwell Slitter Industries
        </p>
        </div>
        {/* Desktop-only value panel (item 4). Hidden below lg, so mobile is the
            single card unchanged. The form itself is not touched. */}
        <aside className="hidden lg:flex lg:flex-col lg:justify-center lg:w-[300px] lg:shrink-0 space-y-5">
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="text-emerald-600 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-600">Setup in {CLAIMS.solveTimeText}, from job entry to a printable shop-floor BOM.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="text-emerald-600 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-600">{CLAIMS.precision} stack precision, no floating-point rounding errors.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={18} className="text-emerald-600 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-600">Works with your existing tooling inventory, only buildable setups.</span>
            </li>
          </ul>
          <p className="text-xs text-slate-500">
            Built by a slitter tooling maker, in business since 1976.
          </p>
        </aside>
      </div>

      {/* Floating WhatsApp button */}
      <a
        href="https://api.whatsapp.com/send?phone=918264621011&text=Hi%2C%20I%20need%20help%20with%20OptiStack"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 
                   bg-[#25D366] hover:bg-[#1fb855] rounded-full shadow-lg hover:shadow-xl
                   transition-all duration-200 hover:scale-110 active:scale-95"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}

